import { FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import { OptimizedBackgroundImage } from "../common/OptimizedImage";
import ViewMoreCard from "../common/ViewMoreCard";
import { useInputMode } from "../../utils/hooks/useInputMode";

const ContentRowScrollingWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 1;
  flex-grow: 1;
  padding: 40px;
  padding-left: 0;
`;

const ContentRowScrollingContent = styled.div`
  display: flex;
  flex-direction: row;
  transition: all ease 0.5s;
`;

const ContentRowWrapper = styled.div`
  margin: 0 2rem;
  position: relative;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  transform: translateY(-7rem);
`;

const ContentRowTitle = styled.h2`
  color: white;
  margin: 0;
  margin-bottom: 10px;
  font-size: 40px;
  font-weight: 900;
  padding-right: 40px;
  padding-top: 20px;
`;

const CardContainer = styled.div`
  min-width: 450px;
  min-height: 250px;
  border-radius: 15px;
  position: relative;
  overflow: hidden;
  margin-left: 2rem;
  cursor: pointer;
`;

const FocusBorderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  border-radius: 15px;
  pointer-events: none;
  z-index: 20;
`;

const ContentOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$focused ? 1 : 0};
  pointer-events: none;
  transition: opacity 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  flex-direction: column;
  gap: 1rem;
  z-index: 15;
`;

const Title = styled.span`
  color: white;
  font-size: 30px;
  font-weight: 700;
  text-align: center;
`;

const Year = styled.span`
  color: white;
  font-size: 32px;
  font-weight: 500;
  text-align: center;
`;

const MovieItem = ({ item = {}, onFocus, isFirst }) => {
  const { contentPhotos, slug, title, year, kind, contentType } = item?.content || {};
  const navigate = useNavigate();
  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () => {
      navigate(`/${contentType?.kind || kind}/${slug}`);
    },
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        setFocus("MENU");
        return false;
      }
      return true;
    },
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized image with LQIP and WebP support
  const imageData = getOptimizedImageForType("infinitySliderCover", contentPhotos, "TV", {
    quality: 90,
    width: 300,
    format: "jpeg",
  });

  return (
    <CardContainer
      ref={ref}
      onClick={() => navigate(`/${contentType?.kind || kind}/${slug}`)}
      onMouseEnter={handleMouseEnter}
    >
      <OptimizedBackgroundImage
        src={imageData.src}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "10px",
        }}
      />
      <FocusBorderOverlay $focused={focused} />
      <ContentOverlay $focused={focused}>
        <Title>{title}</Title>
        <Year>{year}</Year>
      </ContentOverlay>
    </CardContainer>
  );
};

export const Turkilar = ({ movies, onFocus, rowTitle, id, index, actionLink = null, onMoreTap = null }) => {
  const { ref, focusKey } = useFocusable({
    onFocus,
    focusKey: "turkilar_" + id,
  });
  const [selected, setSelected] = useState(null);

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);
  const { isMouseFocus } = useInputMode();
  const onAssetFocus = useCallback(
    (props) => {
      if (isMouseFocus) return; // Skip horizontal scroll on mouse hover
      const { left } = props;
      if (left < 0) {
        innerRef.current.style.transform = `translateX(${Math.abs(left - 300)}px)`;
      } else {
        innerRef.current.style.transform = `translateX(0px)`;
      }
    },
    [scrollingRef, isMouseFocus]
  );

  if (movies.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref} tabIndex={index}>
        <ContentRowTitle>{rowTitle}</ContentRowTitle>
        <ContentRowScrollingWrapper
          ref={scrollingRef}
          style={
            selected === movies.length
              ? {
                marginLeft: "20px",
                paddingRight: 0,
              }
              : selected > 3
                ? {
                  paddingRight: 0,
                }
                : null
          }
        >
          <ContentRowScrollingContent ref={innerRef}>
            {movies?.map((item, idx) => {
              return (
                <MovieItem
                  item={item}
                  key={"tlr_" + item.content?.slug}
                  onFocus={(e) => {
                    setSelected(idx);
                    onAssetFocus(e);
                  }}
                  isFirst={idx === 0}
                />
              );
            })}
            <div style={{ paddingLeft: "2rem" }}>
              <ViewMoreCard
                width={450}
                height={250}
                actionLink={actionLink}
                onMoreTap={onMoreTap}
                focusKey={`view_more_tlr`}
                onFocus={(e) => {
                  setSelected(movies.length);
                  onAssetFocus(e);
                }}
              />
            </div>
          </ContentRowScrollingContent>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
